import { Sport } from '../types/core';

/**
 * Sport-specific statistic definitions
 * Each sport has a list of available stat keys that coaches can record
 */
export const SPORT_STAT_KEYS: Record<Sport, Array<{ key: string; label: string; description?: string }>> = {
  Baseball: [
    { key: '1B', label: 'Singles (1B)', description: 'Single base hits' },
    { key: '2B', label: 'Doubles (2B)', description: 'Double base hits' },
    { key: '3B', label: 'Triples (3B)', description: 'Triple base hits' },
    { key: 'HR', label: 'Home Runs (HR)', description: 'Home runs' },
    { key: 'BB', label: 'Walks (BB)', description: 'Base on balls (batting)' },
    { key: 'SO', label: 'Strikeouts (SO)', description: 'Strikeouts (batting)' },
    { key: 'SB', label: 'Stolen Bases (SB)', description: 'Stolen bases' },
    { key: 'RBI', label: 'Runs Batted In (RBI)', description: 'Runs batted in' },
    { key: 'R', label: 'Runs (R)', description: 'Runs scored' },
    { key: 'H', label: 'Hits (H)', description: 'Total hits (batting)' },
    { key: 'AB', label: 'At Bats (AB)', description: 'At bats' },
    { key: 'HBP', label: 'Hit By Pitch (HBP)', description: 'Hit by pitch' },
    { key: 'SF', label: 'Sacrifice Fly (SF)', description: 'Sacrifice fly' },
    { key: 'IP', label: 'Innings Pitched (IP)', description: 'Innings pitched' },
    { key: 'K', label: 'Strikeouts (K)', description: 'Pitching strikeouts' },
    { key: 'W', label: 'Wins (W)', description: 'Pitching wins' },
    { key: 'L', label: 'Losses (L)', description: 'Pitching losses' },
    { key: 'SV', label: 'Saves (SV)', description: 'Saves' },
    { key: 'ERA', label: 'ERA', description: 'Earned run average' },
    { key: 'H_Allowed', label: 'Hits Allowed', description: 'Hits allowed (pitching)' },
    { key: 'BB_Allowed', label: 'Walks Allowed', description: 'Walks allowed (pitching)' },
    { key: 'R_Allowed', label: 'Runs Allowed', description: 'Runs allowed (pitching)' },
    { key: 'ER', label: 'Earned Runs (ER)', description: 'Earned runs allowed (pitching)' },
    { key: 'BF', label: 'Batters Faced (BF)', description: 'Batters faced (pitching)' },
    { key: 'Balls', label: 'Balls', description: 'Balls thrown (pitch count)' },
    { key: 'Strikes', label: 'Strikes', description: 'Strikes thrown (pitch count)' },
    { key: 'Pitches', label: 'Pitches', description: 'Total pitches thrown' },
    { key: 'HBP_Allowed', label: 'Hit Batters (HBP)', description: 'Hit batters (pitching)' },
    { key: 'WP', label: 'Wild Pitches (WP)', description: 'Wild pitches' },
    { key: 'Balk', label: 'Balks', description: 'Balks' },
  ],
  Softball: [
    { key: '1B', label: 'Singles (1B)', description: 'Single base hits' },
    { key: '2B', label: 'Doubles (2B)', description: 'Double base hits' },
    { key: '3B', label: 'Triples (3B)', description: 'Triple base hits' },
    { key: 'HR', label: 'Home Runs (HR)', description: 'Home runs' },
    { key: 'BB', label: 'Walks (BB)', description: 'Base on balls (batting)' },
    { key: 'SO', label: 'Strikeouts (SO)', description: 'Strikeouts (batting)' },
    { key: 'SB', label: 'Stolen Bases (SB)', description: 'Stolen bases' },
    { key: 'RBI', label: 'Runs Batted In (RBI)', description: 'Runs batted in' },
    { key: 'R', label: 'Runs (R)', description: 'Runs scored' },
    { key: 'H', label: 'Hits (H)', description: 'Total hits (batting)' },
    { key: 'AB', label: 'At Bats (AB)', description: 'At bats' },
    { key: 'HBP', label: 'Hit By Pitch (HBP)', description: 'Hit by pitch' },
    { key: 'SF', label: 'Sacrifice Fly (SF)', description: 'Sacrifice fly' },
    { key: 'IP', label: 'Innings Pitched (IP)', description: 'Innings pitched' },
    { key: 'K', label: 'Strikeouts (K)', description: 'Pitching strikeouts' },
    { key: 'W', label: 'Wins (W)', description: 'Pitching wins' },
    { key: 'L', label: 'Losses (L)', description: 'Pitching losses' },
    { key: 'SV', label: 'Saves (SV)', description: 'Saves' },
    { key: 'ERA', label: 'ERA', description: 'Earned run average' },
    { key: 'H_Allowed', label: 'Hits Allowed', description: 'Hits allowed (pitching)' },
    { key: 'BB_Allowed', label: 'Walks Allowed', description: 'Walks allowed (pitching)' },
    { key: 'R_Allowed', label: 'Runs Allowed', description: 'Runs allowed (pitching)' },
    { key: 'ER', label: 'Earned Runs (ER)', description: 'Earned runs allowed (pitching)' },
    { key: 'BF', label: 'Batters Faced (BF)', description: 'Batters faced (pitching)' },
    { key: 'Balls', label: 'Balls', description: 'Balls thrown (pitch count)' },
    { key: 'Strikes', label: 'Strikes', description: 'Strikes thrown (pitch count)' },
    { key: 'Pitches', label: 'Pitches', description: 'Total pitches thrown' },
    { key: 'HBP_Allowed', label: 'Hit Batters (HBP)', description: 'Hit batters (pitching)' },
    { key: 'WP', label: 'Wild Pitches (WP)', description: 'Wild pitches' },
    { key: 'Balk', label: 'Balks', description: 'Balks' },
  ],
  Football: [
    { key: 'TD', label: 'Touchdowns (TD)', description: 'Touchdowns scored' },
    { key: 'PassYds', label: 'Passing Yards', description: 'Passing yards' },
    { key: 'PassTD', label: 'Passing TDs', description: 'Passing touchdowns' },
    { key: 'PassInt', label: 'Interceptions Thrown', description: 'Interceptions thrown' },
    { key: 'RushYds', label: 'Rushing Yards', description: 'Rushing yards' },
    { key: 'RushTD', label: 'Rushing TDs', description: 'Rushing touchdowns' },
    { key: 'RecYds', label: 'Receiving Yards', description: 'Receiving yards' },
    { key: 'RecTD', label: 'Receiving TDs', description: 'Receiving touchdowns' },
    { key: 'Rec', label: 'Receptions', description: 'Number of receptions' },
    { key: 'Tackles', label: 'Tackles', description: 'Total tackles' },
    { key: 'Sacks', label: 'Sacks', description: 'Quarterback sacks' },
    { key: 'Int', label: 'Interceptions', description: 'Interceptions caught' },
    { key: 'Fumbles', label: 'Fumbles', description: 'Fumbles' },
    { key: 'FumbleRec', label: 'Fumble Recoveries', description: 'Fumble recoveries' },
    { key: 'FG', label: 'Field Goals', description: 'Field goals made' },
    { key: 'XP', label: 'Extra Points', description: 'Extra points made' },
    // Recorded by FootballGameStatsModal (see src/data/footballStatSections.ts)
    { key: 'PassComp', label: 'Pass Completions', description: 'Completed passes' },
    { key: 'PassAtt', label: 'Pass Attempts', description: 'Passes attempted' },
    { key: 'Pass20Plus', label: 'Passes 20+ Yards', description: 'Completions of 20+ yards' },
    { key: 'Pass30Plus', label: 'Passes 30+ Yards', description: 'Completions of 30+ yards' },
    { key: 'Pass40Plus', label: 'Passes 40+ Yards', description: 'Completions of 40+ yards' },
    { key: 'RushAtt', label: 'Rushing Attempts', description: 'Carries' },
    { key: 'RushFumble', label: 'Rushing Fumbles', description: 'Fumbles while rushing' },
    { key: 'BrokenTkl', label: 'Broken Tackles', description: 'Tackles broken by the runner' },
    { key: 'Rush20Plus', label: 'Rushes 20+ Yards', description: 'Carries of 20+ yards' },
    { key: 'Rush30Plus', label: 'Rushes 30+ Yards', description: 'Carries of 30+ yards' },
    { key: 'Rush40Plus', label: 'Rushes 40+ Yards', description: 'Carries of 40+ yards' },
    { key: 'Targets', label: 'Targets', description: 'Times targeted by a pass' },
    { key: 'YAC', label: 'Yards After Catch', description: 'Yards gained after the catch' },
    { key: 'RecFumble', label: 'Receiving Fumbles', description: 'Fumbles after a catch' },
    { key: 'Rec20Plus', label: 'Receptions 20+ Yards', description: 'Catches of 20+ yards' },
    { key: 'Rec30Plus', label: 'Receptions 30+ Yards', description: 'Catches of 30+ yards' },
    { key: 'Rec40Plus', label: 'Receptions 40+ Yards', description: 'Catches of 40+ yards' },
    { key: 'TFL', label: 'Tackles For Loss', description: 'Tackles behind the line of scrimmage' },
    { key: 'PassDef', label: 'Passes Defended', description: 'Passes broken up' },
    { key: 'FF', label: 'Forced Fumbles', description: 'Fumbles forced' },
  ],
  FlagFootball: [
    { key: 'TD', label: 'Touchdowns (TD)', description: 'Touchdowns scored' },
    { key: 'PassYds', label: 'Passing Yards', description: 'Passing yards' },
    { key: 'PassTD', label: 'Passing TDs', description: 'Passing touchdowns' },
    { key: 'PassInt', label: 'Interceptions Thrown', description: 'Interceptions thrown' },
    { key: 'RushYds', label: 'Rushing Yards', description: 'Rushing yards' },
    { key: 'RushTD', label: 'Rushing TDs', description: 'Rushing touchdowns' },
    { key: 'RecYds', label: 'Receiving Yards', description: 'Receiving yards' },
    { key: 'RecTD', label: 'Receiving TDs', description: 'Receiving touchdowns' },
    { key: 'Rec', label: 'Receptions', description: 'Number of receptions' },
    { key: 'Tackles', label: 'Tackles', description: 'Flag pulls/tackles' },
    { key: 'Int', label: 'Interceptions', description: 'Interceptions caught' },
    { key: 'Fumbles', label: 'Fumbles', description: 'Fumbles' },
    // Recorded by FootballGameStatsModal (see src/data/footballStatSections.ts).
    // Sacks, FumbleRec, FG and XP are football-only and stay out of this list.
    { key: 'PassComp', label: 'Pass Completions', description: 'Completed passes' },
    { key: 'PassAtt', label: 'Pass Attempts', description: 'Passes attempted' },
    { key: 'Pass20Plus', label: 'Passes 20+ Yards', description: 'Completions of 20+ yards' },
    { key: 'Pass30Plus', label: 'Passes 30+ Yards', description: 'Completions of 30+ yards' },
    { key: 'Pass40Plus', label: 'Passes 40+ Yards', description: 'Completions of 40+ yards' },
    { key: 'RushAtt', label: 'Rushing Attempts', description: 'Carries' },
    { key: 'RushFumble', label: 'Rushing Fumbles', description: 'Fumbles while rushing' },
    { key: 'BrokenTkl', label: 'Broken Tackles', description: 'Tackles broken by the runner' },
    { key: 'Rush20Plus', label: 'Rushes 20+ Yards', description: 'Carries of 20+ yards' },
    { key: 'Rush30Plus', label: 'Rushes 30+ Yards', description: 'Carries of 30+ yards' },
    { key: 'Rush40Plus', label: 'Rushes 40+ Yards', description: 'Carries of 40+ yards' },
    { key: 'Targets', label: 'Targets', description: 'Times targeted by a pass' },
    { key: 'YAC', label: 'Yards After Catch', description: 'Yards gained after the catch' },
    { key: 'RecFumble', label: 'Receiving Fumbles', description: 'Fumbles after a catch' },
    { key: 'Rec20Plus', label: 'Receptions 20+ Yards', description: 'Catches of 20+ yards' },
    { key: 'Rec30Plus', label: 'Receptions 30+ Yards', description: 'Catches of 30+ yards' },
    { key: 'Rec40Plus', label: 'Receptions 40+ Yards', description: 'Catches of 40+ yards' },
    { key: 'TFL', label: 'Tackles For Loss', description: 'Tackles behind the line of scrimmage' },
    { key: 'PassDef', label: 'Passes Defended', description: 'Passes broken up' },
    { key: 'FF', label: 'Forced Fumbles', description: 'Fumbles forced' },
  ],
  Basketball: [
    { key: 'Points', label: 'Points', description: 'Total points scored' },
    { key: 'Rebounds', label: 'Rebounds', description: 'Total rebounds' },
    { key: 'Assists', label: 'Assists', description: 'Assists' },
    { key: 'Steals', label: 'Steals', description: 'Steals' },
    { key: 'Blocks', label: 'Blocks', description: 'Blocked shots' },
    { key: '3PT', label: '3-Pointers Made', description: 'Three-point field goals made' },
    { key: 'FGM', label: 'Field Goals Made', description: 'Field goals made' },
    { key: 'FGA', label: 'Field Goals Attempted', description: 'Field goals attempted' },
    { key: 'FTM', label: 'Free Throws Made', description: 'Free throws made' },
    { key: 'FTA', label: 'Free Throws Attempted', description: 'Free throws attempted' },
    { key: 'Turnovers', label: 'Turnovers', description: 'Turnovers' },
    { key: 'Fouls', label: 'Fouls', description: 'Personal fouls' },
  ],
  Soccer: [
    { key: 'Goals', label: 'Goals', description: 'Goals scored' },
    { key: 'Assists', label: 'Assists', description: 'Assists' },
    { key: 'Shots', label: 'Shots', description: 'Shots taken' },
    { key: 'ShotsOnGoal', label: 'Shots on Goal', description: 'Shots on target' },
    { key: 'Saves', label: 'Saves', description: 'Goalkeeper saves' },
    { key: 'YellowCards', label: 'Yellow Cards', description: 'Yellow cards received' },
    { key: 'RedCards', label: 'Red Cards', description: 'Red cards received' },
    { key: 'Tackles', label: 'Tackles', description: 'Tackles made' },
    { key: 'Interceptions', label: 'Interceptions', description: 'Interceptions' },
  ],
  Hockey: [
    { key: 'Goals', label: 'Goals', description: 'Goals scored' },
    { key: 'Assists', label: 'Assists', description: 'Assists' },
    { key: 'Points', label: 'Points', description: 'Total points (goals + assists)' },
    { key: 'Shots', label: 'Shots', description: 'Shots on goal' },
    { key: 'Saves', label: 'Saves', description: 'Goalkeeper saves' },
    { key: 'PIM', label: 'Penalty Minutes', description: 'Penalty minutes' },
    { key: 'Hits', label: 'Hits', description: 'Hits delivered' },
    { key: 'Blocks', label: 'Blocks', description: 'Shots blocked' },
  ],
  Volleyball: [
    { key: 'Kills', label: 'Kills', description: 'Kills' },
    { key: 'Assists', label: 'Assists', description: 'Assists' },
    { key: 'Aces', label: 'Aces', description: 'Service aces' },
    { key: 'Digs', label: 'Digs', description: 'Digs' },
    { key: 'Blocks', label: 'Blocks', description: 'Blocks' },
    { key: 'Errors', label: 'Errors', description: 'Errors' },
  ],
  Lacrosse: [
    { key: 'Goals', label: 'Goals', description: 'Goals scored' },
    { key: 'Assists', label: 'Assists', description: 'Assists' },
    { key: 'Points', label: 'Points', description: 'Total points (goals + assists)' },
    { key: 'Shots', label: 'Shots', description: 'Shots taken' },
    { key: 'Saves', label: 'Saves', description: 'Goalkeeper saves' },
    { key: 'GroundBalls', label: 'Ground Balls', description: 'Ground balls recovered' },
    { key: 'Turnovers', label: 'Turnovers', description: 'Turnovers' },
  ],
  Tennis: [
    // Original seven (sc-336/Phase 2) — kept as-is for backward compatibility.
    // BreakPointsWon predates, and is distinct from, the BreakPts* funnel added
    // below (sc-339/Phase 5); both are kept rather than renaming the former.
    { key: 'Aces', label: 'Aces', description: 'Service aces' },
    { key: 'DoubleFaults', label: 'Double Faults', description: 'Double faults' },
    { key: 'Winners', label: 'Winners', description: 'Winning shots' },
    { key: 'UnforcedErrors', label: 'Unforced Errors', description: 'Unforced errors' },
    { key: 'SetsWon', label: 'Sets Won', description: 'Sets won' },
    { key: 'GamesWon', label: 'Games Won', description: 'Games won' },
    { key: 'BreakPointsWon', label: 'Break Points Won', description: 'Break points converted' },
    // Projected from the match_points log (sc-339 / Tennis Phase 5) — see
    // src/services/tennis/statsProjection.ts for exactly how each is derived.
    { key: 'ServePts_1stIn', label: '1st Serves In', description: 'First serves that landed in' },
    { key: 'ServePts_1st', label: 'Service Points', description: 'Total service points played (every point has a first-serve attempt)' },
    { key: 'ServePts_1stWon', label: '1st Serve Points Won', description: 'Points won when the first serve was in' },
    { key: 'ServePts_2nd', label: '2nd Serve Points', description: 'Points that went to (or double-faulted on) a second serve' },
    { key: 'ServePts_2ndWon', label: '2nd Serve Points Won', description: 'Points won on a second serve' },
    { key: 'SvcGames', label: 'Service Games', description: 'Games served' },
    { key: 'SvcGamesHeld', label: 'Service Games Held', description: 'Service games won' },
    { key: 'RetPts', label: 'Return Points', description: 'Points played while returning' },
    { key: 'RetPtsWon', label: 'Return Points Won', description: 'Points won while returning' },
    { key: 'RetGames', label: 'Return Games', description: 'Games played while returning' },
    { key: 'RetGamesWon', label: 'Return Games Won', description: 'Return games won (breaks of serve)' },
    { key: 'BreakPtsFaced', label: 'Break Points Faced', description: 'Break points faced on serve' },
    { key: 'BreakPtsSaved', label: 'Break Points Saved', description: 'Break points saved on serve' },
    { key: 'BreakPtsWon', label: 'Break Points Won', description: 'Break points converted while returning' },
    { key: 'BreakPtsPlayed', label: 'Break Points Played', description: 'Break points played while returning' },
    { key: 'ForcedErrors', label: 'Forced Errors', description: 'Forced errors' },
    // Error-location breakdown of UnforcedErrors — needed for the "long errors" insight
    // card trigger; not itemized in the original Phase 5 key list but required by it.
    { key: 'UnforcedErrors_Net', label: 'Unforced Errors (Net)', description: 'Unforced errors into the net' },
    { key: 'UnforcedErrors_Long', label: 'Unforced Errors (Long)', description: 'Unforced errors hit long' },
    { key: 'UnforcedErrors_Wide', label: 'Unforced Errors (Wide)', description: 'Unforced errors hit wide' },
    { key: 'NetPts', label: 'Net Points', description: 'Points played at net' },
    { key: 'NetPtsWon', label: 'Net Points Won', description: 'Points won at net' },
    { key: 'Rally_1to3', label: 'Rally 1-3 Shots', description: 'Points in rallies of 1-3 shots' },
    { key: 'Rally_1to3Won', label: 'Rally 1-3 Shots Won', description: 'Points won in rallies of 1-3 shots' },
    { key: 'Rally_4to6', label: 'Rally 4-6 Shots', description: 'Points in rallies of 4-6 shots' },
    { key: 'Rally_4to6Won', label: 'Rally 4-6 Shots Won', description: 'Points won in rallies of 4-6 shots' },
    { key: 'Rally_7to9', label: 'Rally 7-9 Shots', description: 'Points in rallies of 7-9 shots' },
    { key: 'Rally_7to9Won', label: 'Rally 7-9 Shots Won', description: 'Points won in rallies of 7-9 shots' },
    { key: 'Rally_10Plus', label: 'Rally 10+ Shots', description: 'Points in rallies of 10+ shots' },
    { key: 'Rally_10PlusWon', label: 'Rally 10+ Shots Won', description: 'Points won in rallies of 10+ shots' },
    { key: 'SetsLost', label: 'Sets Lost', description: 'Sets lost' },
    { key: 'GamesLost', label: 'Games Lost', description: 'Games lost' },
    { key: 'MatchWon', label: 'Matches Won', description: 'Matches won' },
    { key: 'TiebreaksWon', label: 'Tiebreaks Won', description: 'Tiebreaks won' },
    { key: 'TiebreaksPlayed', label: 'Tiebreaks Played', description: 'Tiebreaks played' },
  ],
};

/**
 * Get available stat keys for a sport
 */
export function getStatKeysForSport(sport: Sport): Array<{ key: string; label: string; description?: string }> {
  return SPORT_STAT_KEYS[sport] || [];
}

